import { Icon, IconButton } from '@smartgift/frontend-toolkit';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { dateLabelFormatDay, dateLabelFormatDayWithYear } from '@hero/constants';
import { Table, UserName } from '@hero/molecules';
import { useGetContacts, useGetTagList } from '@hero/services/contact-service/hooks';
import { checkFormatContainsYear } from '@hero/utils/dateUtils';

const BasicContactTable = ({ ids, disabledColumns = [], actionButtons = {} }) => {
  const { t } = useTranslation();

  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [globalFilters, setGlobalFilters] = useState({});
  const { data: filterOptions } = useGetTagList();
  const { data: contacts, isLoading } = useGetContacts(
    {
      filters: {
        searchTerm: globalFilters.searchTerm,
        tags: globalFilters.filters?.map(item => item.value),
        ids
      },
      pagination: {
        page: pageIndex + 1,
        limit: pageSize
      }
    },
    {
      enabled: ids?.length > 0,
      select(response) {
        if (ids.length === 0) {
          return {};
        }
        return response;
      }
    }
  );

  function firstNameCell({
    row: {
      original: { firstName, lastName }
    }
  }) {
    return <UserName firstName={firstName} lastName={lastName} hasNameAvatar />;
  }

  const columns = React.useMemo(
    () =>
      [
        {
          header: t('name'),
          size: 300,
          accessorKey: 'firstName',
          cell: firstNameCell
        },
        {
          id: 'email',
          header: t('email'),
          accessorKey: 'email'
        },
        {
          id: 'birthday',
          header: t('birthday'),
          editable: false,
          size: 200,
          type: 'mask',
          accessorFn: e => e.birthday?.full,
          componentProps: {
            formatPreview: v => {
              return v
                ? dayjs(v).format(
                    checkFormatContainsYear(v) ? dateLabelFormatDayWithYear : dateLabelFormatDay
                  )
                : '';
            }
          }
        },
        {
          id: 'anniversary',
          header: t('workAnniversary'),
          editable: false,
          size: 200,
          type: 'mask',
          accessorFn: e => e.anniversary?.full,
          componentProps: {
            formatPreview: v => {
              return v
                ? dayjs(v).format(
                    checkFormatContainsYear(v) ? dateLabelFormatDayWithYear : dateLabelFormatDay
                  )
                : '';
            }
          }
        },
        {
          id: 'tags',
          header: 'Tag',
          accessorFn: originalRow => {
            return originalRow?.tags?.map(tag => ({
              label: tag.name,
              value: tag.id
            }));
          },
          type: 'tags'
        },
        ...(actionButtons?.delete
          ? [
              {
                id: 'actions',
                header: '',
                size: 50,
                cell: ({ row }) => {
                  return (
                    <IconButton
                      variant="text"
                      color="neutral"
                      onClick={() => actionButtons.delete.onClick(row.id, ids)}
                    >
                      <Icon name="delete" size="md" />
                    </IconButton>
                  );
                }
              }
            ]
          : [])
      ].filter(column => !disabledColumns.includes(column.id)),
    [ids]
  );

  return (
    <Table
      columns={columns}
      data={contacts?.docs || []}
      globalFilters={globalFilters}
      pagination={{
        pageIndex,
        pageSize,
        total: contacts?.pagination?.total
      }}
      manualPagination
      loading={isLoading && contacts?.docs?.length > 0}
      setGlobalFilters={setGlobalFilters}
      setPagination={setPagination}
      pageCount={contacts?.pagination?.pages}
      enabled={{ filter: true, header: true }}
      rowHeight={52}
      filterProps={{
        placeholder: t('filterByType', { type: t('tags') }),
        searchPlaceholder: t('searchTagOnFilterPlaceholder')
      }}
      filterOptions={filterOptions?.docs?.map(doc => ({ label: doc.name, value: doc.id })) || []}
      components={{
        SelectionHead: null,
        SecondHead: null
      }}
    />
  );
};

export { BasicContactTable };
